import { useEffect, useRef } from 'react';
import { IChartApi, ISeriesApi, LineSeries } from 'lightweight-charts';
import { Candle } from '@shared/types';

interface DonchianOverlayProps {
  chart: IChartApi;
  candles: Candle[];
  entryLookback?: number;  // default 20
  visible: boolean;
}

interface LineRefs {
  upper20: ISeriesApi<'Line'> | null;
  lower20: ISeriesApi<'Line'> | null;
}

/**
 * DonchianOverlay renders 2 Donchian channel line series on a TradingView chart:
 *   - Upper 20: green dashed (LineStyle 2)
 *   - Lower 20: red dashed (LineStyle 2)
 *
 * Channels are calculated from candle highs/lows excluding the current (incomplete) candle.
 */
export function DonchianOverlay({
  chart,
  candles,
  entryLookback = 20,
  visible,
}: DonchianOverlayProps) {
  const seriesRefs = useRef<LineRefs>({
    upper20: null,
    lower20: null,
  });

  // Helper: create 2 line series and store refs
  const createSeries = (c: IChartApi): LineRefs => {
    const upper20 = c.addSeries(LineSeries, {
      color: '#22c55e',
      lineWidth: 1,
      lineStyle: 2, // dashed
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });

    const lower20 = c.addSeries(LineSeries, {
      color: '#ef4444',
      lineWidth: 1,
      lineStyle: 2, // dashed
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });

    return { upper20, lower20 };
  };

  // Helper: remove all series from chart
  const removeSeries = (c: IChartApi, refs: LineRefs) => {
    try { if (refs.upper20) c.removeSeries(refs.upper20); } catch { /* ignore */ }
    try { if (refs.lower20) c.removeSeries(refs.lower20); } catch { /* ignore */ }
    refs.upper20 = null;
    refs.lower20 = null;
  };

  // Helper: compute channel data from candles
  const computeChannels = (
    candleArr: Candle[],
    entry: number
  ) => {
    const upper20Data: { time: number; value: number }[] = [];
    const lower20Data: { time: number; value: number }[] = [];

    for (let i = 0; i < candleArr.length; i++) {
      const timeSec = Math.floor(candleArr[i].timestamp / 1000);

      // 20-bar entry channel: look at prior `entry` candles (exclude current)
      if (i >= entry) {
        const slice20 = candleArr.slice(i - entry, i);
        const h20 = Math.max(...slice20.map((c) => c.high));
        const l20 = Math.min(...slice20.map((c) => c.low));
        upper20Data.push({ time: timeSec, value: h20 });
        lower20Data.push({ time: timeSec, value: l20 });
      }
    }

    // lightweight-charts asserts strictly ascending time and THROWS on a
    // repeat — and that throw propagates to the error boundary, blanking the
    // entire chart rather than just this overlay. A single duplicate candle
    // from the engine did exactly that on 2026-08-01.
    //
    // The engine no longer serves duplicates, but one overlay must not be able
    // to take the chart down, so this is belt as well as braces.
    const ascending = (rows: { time: number; value: number }[]) => {
      const out: { time: number; value: number }[] = [];
      for (const r of rows) {
        const prev = out[out.length - 1];
        if (prev && prev.time === r.time) out[out.length - 1] = r;
        else if (prev && prev.time > r.time) continue;
        else out.push(r);
      }
      return out;
    };

    return { upper20Data: ascending(upper20Data), lower20Data: ascending(lower20Data) };
  };

  // Main effect: manage visibility and data
  useEffect(() => {
    const refs = seriesRefs.current;

    if (!visible) {
      // Remove series if they exist
      if (refs.upper20 !== null) {
        removeSeries(chart, refs);
      }
      return;
    }

    if (candles.length === 0) return;

    // Create series if not yet created
    if (refs.upper20 === null) {
      const created = createSeries(chart);
      refs.upper20 = created.upper20;
      refs.lower20 = created.lower20;
    }

    // Compute and set channel data
    const { upper20Data, lower20Data } = computeChannels(candles, entryLookback);

    if (upper20Data.length > 0 && refs.upper20 && refs.lower20) {
      refs.upper20.setData(upper20Data as any);
      refs.lower20.setData(lower20Data as any);
    }
  }, [chart, candles, entryLookback, visible]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      removeSeries(chart, seriesRefs.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
